import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import {
  VehicleAttributeCategory,
  VehicleAttributeValueEntity,
  VehicleAttributeValueRequest,
  VehicleAttributeValuesByCategory,
  VehicleAttributeValueUsageEntity,
} from 'src/entities/VehicleAttributeValue.entity';

const requestPathBase = '/api/v2/vehicle_attribute_values';
export const queryKey = 'useVehicleAttributeValuesRequest';

const getResources = async (companyId?: number) => {
  const requestPath = companyId
    ? `${requestPathBase}?company_id=${companyId}`
    : requestPathBase;
  const response: AxiosResponse<VehicleAttributeValuesByCategory> = await axios
    .get<VehicleAttributeValuesByCategory>(requestPath);

  return response.data;
};

/**
 * 会社を指定したときだけキーに足す。
 *
 * Why not つねに [queryKey, companyId] にする: 省略時が [queryKey, undefined] になり、
 * 設定画面（会社を渡さない）のキャッシュを指している `setQueryData([queryKey])` が
 * 黙って当たらなくなる。前方一致は保たれるので、改名ジョブの reset_key
 * （`['useVehicleAttributeValuesRequest']`）は会社ぶんのキーにも届く。
 */
const listQueryKey = (companyId?: number) => (companyId ? [queryKey, companyId] : [queryKey]);

/**
 * 車両表示設定の選択肢をカテゴリごとに取得する。#329
 *
 * 無効な値も含めて返ってくる（設定画面は「無効」として一覧に出す）。
 *
 * @param companyId 選択肢を引く会社。**編集するレコード自身の会社**を渡す。
 *   全社表示では一覧に他社のレコードが混ざるので、選択中の会社で引くと
 *   他社にしか無い値が選択肢に出ない（現在値は withCurrentValues が残すので消えないが、
 *   一度外すと選び直せず、ログイン会社にしか無い値を選ぶと保存が 400 になる）。
 *   省略・0 のときはログイン会社ぶん（従来どおり）。
 *
 *   Why not 0 も送る: 0 は「事業所をまだ選んでいない」状態の値で、会社 ID ではない。
 *   送ると API の selectable_company_ids 検証に落ちて 403 になる。
 */
export const useVehicleAttributeValuesRequest = (companyId?: number) => useQuery({
  queryKey: listQueryKey(companyId),
  queryFn: () => getResources(companyId),
  staleTime: Infinity,
});

const getUsage = async (id: number) => {
  const response: AxiosResponse<VehicleAttributeValueUsageEntity> = await axios
    .get<VehicleAttributeValueUsageEntity>(`${requestPathBase}/${id}/usage`);

  return response.data;
};

/**
 * 改名ダイアログの警告に出す使用件数を取得する。#329 P6
 *
 * 本番最大テナントで 3〜4 秒かかるので、改名しようとするまで（enabled が立つまで）引かない。
 * ダイアログは件数を待たずに開き、改名操作も件数を待たずにできる。
 *
 * Why not staleTime を 0 にする: 改名を打ち直して enabled が立ち直すたびに
 * 3〜4 秒のリクエストが積まれる。ダイアログを開いている間は 1 回で足りる。
 * Why not gcTime を既定（5 分）のままにする: 件数は取込が走るたびに陳腐化する。
 * ダイアログを閉じて誰も見なくなったら捨て、開き直したら取り直す。
 * Why not retry する: 重いエンドポイントを 3 回叩き直すより、
 * 件数が出せなかったことを警告に書いて改名させたほうがよい。
 */
export const useVehicleAttributeValueUsageRequest = (
  id: number | null,
  enabled: boolean,
) => useQuery({
  queryKey: [queryKey, 'usage', id],
  queryFn: () => getUsage(id),
  enabled: enabled && id !== null,
  staleTime: Infinity,
  gcTime: 0,
  retry: false,
});

export type AddVehicleAttributeValueParams = VehicleAttributeValueRequest & {
  category: VehicleAttributeCategory;
};

export type UpdateVehicleAttributeValueParams = VehicleAttributeValueRequest & {
  id: number;
};

export type ReorderVehicleAttributeValuesParams = {
  category: VehicleAttributeCategory;
  /** そのカテゴリの id を全件、並べたい順に渡す。欠けていると API が 400 を返す */
  ids: number[];
};

/**
 * 書き込みが効くキャッシュ。
 *
 * 追加・変更・並べ替えの API は company_id を受けない（設定画面からの操作なので、
 * つねにログイン会社のマスタを書き換える）。だから楽観更新も無効化も、
 * **会社を渡さないキー**＝設定画面が読んでいるキャッシュだけを見ればよい。
 *
 * Why not `[queryKey]` と直接書く: 会社スコープ（#329）で読み手のキーが
 * `[queryKey, companyId]` にもなりうるので、両方に同じ配列リテラルを書くと
 * 「たまたま一致している」状態になる。読み手と同じ組み立てを通しておけば、
 * キーの形を変えたときに片側だけ取り残されない。
 */
const writtenQueryKey = listQueryKey();

/**
 * 車両表示設定は会社設定（画面下の 1 つの保存ボタン）とは別 API で即時保存する。
 * そのため mutation ごとに一覧を無効化して取り直す。
 */
/** カテゴリをまたいで 1 件だけ差し替える。どのカテゴリの値かは呼び出し側が知らなくてよい */
const mapValueById = (
  current: VehicleAttributeValuesByCategory,
  id: number,
  update: (value: VehicleAttributeValueEntity) => VehicleAttributeValueEntity,
): VehicleAttributeValuesByCategory => Object.fromEntries(
  Object.entries(current).map(([category, values]) => [
    category,
    values.map((it) => (it.id === id ? update(it) : it)),
  ]),
) as VehicleAttributeValuesByCategory;

/** カテゴリをまたいで 1 件だけ探す。どのカテゴリの値かは呼び出し側が知らなくてよい */
const findValueById = (
  current: VehicleAttributeValuesByCategory,
  id: number,
): VehicleAttributeValueEntity | undefined => Object.values(current)
  .flat()
  .find((it) => it.id === id);

export const useMutationVehicleAttributeValues = () => {
  const queryClient = useQueryClient();
  // 前方一致なので、会社ぶんのキャッシュ（`[queryKey, companyId]`）もまとめて無効化される
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [queryKey] });

  return {
    addVehicleAttributeValue: useMutation({
      // position はサーバーが「そのカテゴリの最大 position + 1」で決めるので送らない
      // Why not 個別に分割代入する: ここで受けたキーだけが送られるので、
      // リクエスト型へ項目を足しても分割代入を直し忘れると黙って送られなくなる
      // （#329 で実際に踏んだ）。id 以外はそのまま渡す
      mutationFn: ({ ...attributes }: AddVehicleAttributeValueParams) => axios.post(requestPathBase, {
        vehicle_attribute_value: attributes,
      }),

      onSuccess: () => invalidate()
    }),
    updateVehicleAttributeValue: useMutation({
      // category は送らない（別カテゴリへ移す操作は画面に無い）。
      // 改名時の旧表示名のエイリアス追加もサーバーがやるので FE では足さない
      mutationFn: ({ id, ...attributes }: UpdateVehicleAttributeValueParams) => axios
        .patch(`${requestPathBase}/${id}`, {
          vehicle_attribute_value: attributes,
        }),

      /**
       * 有効・無効だけ先にキャッシュへ反映する。
       *
       * 取り直しを待つと、ボタンを押しても状態のバッジが数百 ms 変わらず「効いていない」と読めて
       * 二度押しを誘う。失敗したら onError で戻すので、画面と DB がずれたままにはならない。
       *
       * Why not 表示名やエイリアスも先に反映する: 改名はサーバーが旧表示名をエイリアスへ足し、
       * 既存データの書き換えジョブを積んで updating を立てる。先に名前だけ変えると
       * 「反映中」が付く前の姿を一瞬見せることになり、かえって実態と食い違う
       */
      onMutate: async ({ id, active }) => {
        // 先に飛んでいた取り直しが、あとから到着して楽観更新を古い値で上書きするのを防ぐ
        await queryClient.cancelQueries({ queryKey: writtenQueryKey, exact: true });

        const previous = queryClient.getQueryData<VehicleAttributeValuesByCategory>(writtenQueryKey);
        const previousActive = previous ? findValueById(previous, id)?.active : undefined;

        queryClient.setQueryData<VehicleAttributeValuesByCategory>(
          writtenQueryKey,
          (current) => (current ? mapValueById(current, id, (it) => ({ ...it, active })) : current),
        );

        return { previousActive };
      },

      /**
       * 失敗したら**その行の有効・無効だけ**元へ戻す。
       *
       * Why not 一覧まるごとのスナップショットを戻す: 複数行を続けて切り替えると、
       * 先に始めた操作の失敗があとから届く。まるごと戻すと、その間に成功した別の行まで
       * 古い値へ巻き戻る。戻す範囲は、その mutation が変えた 1 行 1 項目に限る。
       */
      onError: (_error, { id }, context) => {
        const previousActive = context?.previousActive;
        if (previousActive === undefined) return;

        queryClient.setQueryData<VehicleAttributeValuesByCategory>(
          writtenQueryKey,
          (current) => (current
            ? mapValueById(current, id, (it) => ({ ...it, active: previousActive }))
            : current),
        );
      },

      /**
       * 成功・失敗のどちらでも取り直す。
       *
       * Why not 成功したときだけ: staleTime が Infinity なので、失敗して戻したキャッシュは
       * 誰かが取り直すまでサーバーとずれたまま残る。戻した値がサーバーの実体と一致する
       * 保証も無い（別の管理者が同時に直しているかもしれない）。
       */
      onSettled: () => invalidate()
    }),
    reorderVehicleAttributeValues: useMutation({
      mutationFn: async ({ category, ids }: ReorderVehicleAttributeValuesParams) => {
        const response = await axios.patch<VehicleAttributeValueEntity[]>(
          `${requestPathBase}/reorder`,
          { category, ids },
        );
        return response.data;
      },

      // 取り直しを待つとドロップ直後に古い並びが一瞬残るので、
      // レスポンス（そのカテゴリの並べ替え後の全件）でキャッシュを差し替えてから無効化する
      onSuccess: (values, { category }) => {
        queryClient.setQueryData<VehicleAttributeValuesByCategory>(
          writtenQueryKey,
          (current) => (current ? { ...current, [category]: values } : current),
        );
        return invalidate();
      }
    })
  };
};
